import * as React from 'react'

import { Box, Button, Icon, Skeleton, Text } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import {
  AllowanceDataFragment,
  BulkAssignEmployeesTimeoffPolicyMappingsInput,
  ModalFetchPolicyAllowancesDocument,
  TimeoffPoliciesMoveEmployeeModalPageQuery,
  TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument,
  TimeoffPolicyEmployeesCollectionListDocument,
} from 'generated/resources/graphql'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'
import PolicyForm from 'modules/timeoff/lib/TimeoffPolicy/Select'

interface Props {
  loading: boolean
  data: TimeoffPoliciesMoveEmployeeModalPageQuery | undefined
  onClose: () => void
}

interface FormState {
  step: 'select_policy' | 'map_allowances' | 'adjustment_loss'
  timeoffPolicyAllowances: AllowanceDataFragment[]
  mappings: BulkAssignEmployeesTimeoffPolicyMappingsInput[]
}

const modalDescription = (formState: FormState) => {
  switch (formState.step) {
    case 'adjustment_loss':
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
    case 'map_allowances':
      return i18n.t('settings.timeoff_policies.move_employee_modal.description_complete')
    default:
      return i18n.t('settings.timeoff_policies.move_employee_modal.description')
  }
}

const Content = ({
  formState,
  setFormState,
  data,
  policyForm,
}: {
  formState: FormState
  setFormState: (formState: FormState) => void
  data: TimeoffPoliciesMoveEmployeeModalPageQuery | undefined
  policyForm: PolicyForm
}) => {
  const [fetchPolicyAllowances] = useLazyQuery(ModalFetchPolicyAllowancesDocument)

  const currentPolicy = data?.timeoff.currentPolicy

  if (!currentPolicy) {
    throw new Error('Current policy not found')
  }

  const policiesData =
    data?.timeoff.policies.filter((policy) => policy.id !== currentPolicy.id) ?? []

  const employeeData = data?.employees.employee

  if (!employeeData) {
    throw new Error('Employee data not found')
  }

  const employeeHasAllowanceIncidences =
    employeeData.timeoffAllowanceIncidences.filter(
      (incidence) => incidence.timeoffAllowance?.timeoffPolicyId === currentPolicy.id
    ).length > 0

  const options = policiesData.map(({ name, id }) => ({
    label: name,
    value: id,
  }))

  const onSelect = async (field: Field) => {
    const { data: allowanceData } = await fetchPolicyAllowances({
      variables: {
        timeoffPolicyId: String(field.value),
      },
    })
    setFormState({
      ...formState,
      timeoffPolicyAllowances: allowanceData?.timeoff.policy?.allowances ?? [],
    })
  }

  return (
    <>
      {formState.step === 'select_policy' && (
        <>
          <FormRow>
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceEvenly"
              gap="s8"
              paddingTop="s24"
            >
              <Box minWidth="s240" paddingLeft="s4">
                <Text size="300" weight="medium" color="darker800">
                  {i18n.t('settings.timeoff_policies.manual_policy_mapper.current_policy')}
                </Text>
              </Box>
              <Box width="s200">
                <Text size="300" weight="medium" color="darker800">
                  {i18n.t('settings.timeoff_policies.manual_policy_mapper.new_policy')}
                </Text>
              </Box>
            </Box>
          </FormRow>
          <FormRow>
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceEvenly"
              gap="s8"
              paddingBottom="s24"
            >
              <Box minWidth="s200">
                <Text size="300" weight="normal" color="darker800">
                  {currentPolicy.name}
                </Text>
              </Box>
              <Box alignItems="center">
                <Icon.Medium icon="arrow-right" />
              </Box>
              <Box width="s200">
                <Select
                  none={i18n.t('common.none')}
                  field={policyForm.get('timeoff_policy_id')}
                  onChange={(field) => onSelect(field)}
                  options={options}
                />
              </Box>
            </Box>
          </FormRow>
          {employeeHasAllowanceIncidences &&
            currentPolicy.allowances.length > 0 &&
            formState.timeoffPolicyAllowances.length > 0 && (
              <FormRow>
                <AllowanceMapper
                  policyId={currentPolicy.id}
                  allowancesToMap={currentPolicy.allowances}
                  timeoffPolicyAllowances={formState.timeoffPolicyAllowances}
                  onMappingsChange={(mappings) => setFormState({ ...formState, mappings })}
                />
              </FormRow>
            )}
        </>
      )}
    </>
  )
}

const Footer = ({
  formState,
  setFormState,
  loading,
  policyForm,
  data,
  onClose,
}: {
  formState: FormState
  setFormState: (formState: FormState) => void
  loading: boolean
  policyForm: PolicyForm
  data: TimeoffPoliciesMoveEmployeeModalPageQuery | undefined
  onClose: () => void
}) => {
  const [bulkAssignEmployees] = useMutation(TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument, {
    extractorFn: (mutationData) => mutationData.timeoffMutations.bulkAssignEmployeesTimeoffPolicy,
  })

  if (loading) {
    return <Skeleton height="s24" />
  }

  const currentPolicy = data?.timeoff.currentPolicy

  if (!currentPolicy) {
    throw new Error('Current policy not found')
  }

  const employeeData = data?.employees.employee

  if (!employeeData) {
    throw new Error('Employee data not found')
  }

  const employeeHasAllowanceIncidences =
    employeeData.timeoffAllowanceIncidences.filter(
      (incidence) => incidence.timeoffAllowance?.timeoffPolicyId === currentPolicy.id
    ).length > 0

  const onSubmit = async () => {
    const selectedPolicyId = String(policyForm.get('timeoff_policy_id').value)
    await bulkAssignEmployees({
      variables: {
        employeeIds: [employeeData.id],
        mappings: formState.mappings,
        policyId: selectedPolicyId,
      },
      refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
    })
    onClose()
  }

  return (
    <>
      {formState.step === 'select_policy' && (
        <Button
          disabled={loading}
          onClick={async () => {
            if (
              !employeeHasAllowanceIncidences ||
              (formState.mappings.length > 0 &&
                formState.mappings.every((mapping) => mapping.newAllowanceId !== '0'))
            ) {
              return onSubmit()
            } else {
              setFormState({
                ...formState,
                step: 'adjustment_loss',
              })
            }
          }}
        >
          {i18n.t('common.submit')}
        </Button>
      )}
      {formState.step === 'adjustment_loss' && (
        <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
          <Button
            disabled={loading}
            type="positive"
            onClick={() => {
              setFormState({
                ...formState,
                step: 'select_policy',
              })
            }}
          >
            {i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button')}
          </Button>
          <Button
            disabled={loading}
            type="negative"
            onClick={() => {
              return onSubmit()
            }}
          >
            {i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button')}
          </Button>
        </Box>
      )}
    </>
  )
}

const Title = ({
  formState,
  data,
  loading,
}: {
  formState: FormState
  data: TimeoffPoliciesMoveEmployeeModalPageQuery | undefined
  loading: boolean
}) => {
  if (loading) {
    return <Skeleton height="s24" />
  }

  const employeeData = data?.employees.employee

  if (!employeeData) {
    throw new Error('Employee data not found')
  }

  const title =
    formState.step === 'adjustment_loss'
      ? i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
      : i18n.t('settings.timeoff_policies.move_employee_modal.title', {
          employee: employeeData.fullName,
        })

  return <>{title}</>
}

const MoveEmployee = ({ loading, data, onClose }: Props) => {
  const [formState, setFormState] = React.useState<FormState>({
    step: 'select_policy',
    timeoffPolicyAllowances: [],
    mappings: [],
  })
  const policyForm = React.useMemo(() => new PolicyForm({}), [])

  const description = modalDescription(formState)

  const illustration = formState.step === 'adjustment_loss' ? 'alert' : 'document-edit'

  return (
    <Modal
      illustration={illustration}
      // NOTE: title should accept a React element
      title={(<Title formState={formState} data={data} loading={loading} />) as unknown as string}
      description={description}
      onClose={onClose}
      footer={
        <Footer
          formState={formState}
          setFormState={setFormState}
          loading={loading}
          policyForm={policyForm}
          data={data}
          onClose={onClose}
        />
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Content
          formState={formState}
          setFormState={setFormState}
          data={data}
          policyForm={policyForm}
        />
      )}
    </Modal>
  )
}

export default MoveEmployee
